import { AfterViewInit, ChangeDetectorRef, Component, effect, ElementRef, EventEmitter, HostListener, inject, Input, input, OnInit, signal, ViewChild } from '@angular/core';
import { UserService } from '../../services/user.service';
import { CommonModule } from '@angular/common';
import { ChatUserComponent } from '../chat-user/chat-user.component';


@Component({
  selector: 'app-chat-input',
  standalone: true,
  templateUrl: './chat-input.component.html',
  styleUrl: './chat-input.component.scss',
  imports: [
    CommonModule,
    ChatUserComponent
  ]
})
export class ChatInputComponent implements OnInit, AfterViewInit {

  @Input() inputSignal = signal('');

  private readonly userService = inject(UserService);
  private readonly cdr = inject(ChangeDetectorRef);

  
  public selectedUserIndex = 0;
  public filteredUsers: any[] = [];
  public users: any[] = [];
  public mentioning = false;
  public sentForm = new EventEmitter<boolean>();

  @ViewChild('editableInput') editableInput: ElementRef;
  @ViewChild('userSuggestions') userSuggestions: ElementRef;

  constructor() {
    effect(() => {
      if(this.inputSignal().length == 0 && this.editableInput){
        this.reset();
      }
    });
    
  }

  ngOnInit(): void {
    this.userService.getUsers().subscribe((users) => {
      this.users = users.filter((user) => user.role);
      this.filteredUsers = users.filter((user) => user.role);
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    //this.editableInput.nativeElement.focus();
  }

  public focus() {
    this.editableInput.nativeElement.focus();
  }

  filterUsers(query: string) {
    this.selectedUserIndex = 0;
    this.filteredUsers = this.users.filter(user =>
      user.name.toLowerCase().includes(query.toLowerCase()) ||
      user.role.name.toLowerCase().includes(query.toLowerCase())
    );
  }

  onInputChange(event: any) {
    const text = this.editableInput.nativeElement.innerText;
    if(text[text.length - 1] === '@') {
      this.mentioning = true;
      this.selectedUserIndex = 0;
    } else if (text[text.length - 1] === ' ') {
      this.mentioning = false;
      this.selectedUserIndex = 0;
    }
    const index = text.lastIndexOf('@');
    const atCount = (text.match(/@/g) || []).length;
    const mentionCount = (this.inputSignal().match(/\(user:\d+\)/g) || []).length;
    if (this.mentioning && index !== -1 && atCount > mentionCount) {
      this.mentioning = true;
      const query = text.substring(index + 1);
      this.filterUsers(query);
    } else {
      this.mentioning = false;
      const htmlContent = this.editableInput.nativeElement.innerHTML;
      this.inputSignal.set(this.extractMentionsText(htmlContent));
      
    }
    this.cdr.markForCheck();
  }

  addMention(user) {
    const textArea = this.editableInput.nativeElement;
    const index = textArea.innerHTML.lastIndexOf('@');
    if (index !== -1 && user) {
      const beforeText = this.extractMentionsText(textArea.innerHTML.substring(0, index));
      this.inputSignal.set(beforeText + `(user:${user.id})`);
      textArea.innerHTML = this.inputSignal().replace(/\(user:(\d+)\)/g, (match, userId) => {
        const foundUser = this.users.find(u => u.id === parseInt(userId));
        return foundUser ? `<a class="mention" data-user-id="${userId}">@${foundUser.name.trim()}</a>` : match;
      })+ '&nbsp;';
      this.moveCaretToEnd();
      this.mentioning = false;
      this.cdr.markForCheck();
    }else if(!user){
      textArea.innerHTML = textArea.innerHTML.substring(0, index);
      this.moveCaretToEnd();
      this.mentioning = false;
      this.cdr.markForCheck();
    }
  }

  scrollToMention(){
    //scroll
    const userList = this.userSuggestions.nativeElement;
    const selectedUser = this.userSuggestions.nativeElement.children[this.selectedUserIndex];
    if (selectedUser) {
      const userListRect = userList.getBoundingClientRect();
      const selectedUserRect = selectedUser.getBoundingClientRect();
      if (selectedUserRect.bottom > userListRect.bottom) {
        userList.scrollTop += selectedUserRect.height;
      } else if (selectedUserRect.top < userListRect.top) {
        userList.scrollTop -= selectedUserRect.height;
      }
    }
  }

  moveCaretToEnd() {
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(this.editableInput.nativeElement);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
    this.editableInput.nativeElement.focus();
  }

  handleBackspace() {
    const selection = window.getSelection();
    if(selection.anchorNode && selection.anchorNode.parentElement.nodeName === 'A' && selection.anchorNode.parentElement.classList.contains('mention')){
      const mentionElement = selection.anchorNode.parentElement;
      mentionElement.remove();
      this.updateMessageText();
    }
  }
  
  updateMessageText() {
    const htmlContent = this.editableInput.nativeElement.innerHTML;
    this.inputSignal.set(this.extractMentionsText(htmlContent));
    this.moveCaretToEnd();
  }

  extractMentionsText(html: string): string {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
  
    const mentions = tempElement.querySelectorAll('.mention');
    mentions.forEach((mention) => {
      const userId = mention.getAttribute('data-user-id');
      const mentionText = `(user:${userId})`;
      mention.replaceWith(mentionText);
    });
  
    return tempElement.innerText;
  }
  
  onKeyUp(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.mentioning = false;
      this.selectedUserIndex = 0;
    }
    if (this.mentioning && event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.selectedUserIndex === null) {
        this.selectedUserIndex = 0;
      } else {
        this.selectedUserIndex = Math.min(this.selectedUserIndex + 1, this.filteredUsers.length - 1);
      }
      this.cdr.markForCheck();
      this.scrollToMention();
    }
    if(this.mentioning && event.key === 'ArrowUp'){
      event.preventDefault();
      if(this.selectedUserIndex === null) {
        this.selectedUserIndex = 0;
      } else {
        this.selectedUserIndex = Math.max(this.selectedUserIndex - 1, 0);
      }
      this.cdr.markForCheck();
      this.scrollToMention();
    }
    if(this.mentioning && event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.mentioning && event.key === 'Tab') {
      event.preventDefault();
      if (this.selectedUserIndex !== null) {
        const user = this.filteredUsers[this.selectedUserIndex];
        this.addMention(user);
      }
    }
    if (event.key === 'Backspace' && !this.mentioning) {
      this.handleBackspace();
    }
    if (!this.mentioning && (event.key === '@' || event.keyCode === 50 && event.shiftKey)) {
      // Detectamos el inicio de la mención también en el evento onKeyUp
      this.mentioning = true;
      this.selectedUserIndex = 0;
    }
  }
  onKeyDown(event: KeyboardEvent) {
    const bannedKeys = ['ArrowUp', 'ArrowDown', 'Tab'];
    if (this.mentioning && bannedKeys.includes(event.key)) {
      event.preventDefault();
    }
    if (!this.mentioning && (event.key === '@' || event.keyCode === 50 && event.shiftKey)) {
      // Detectamos el inicio de la mención en el evento onKeyDown
      this.mentioning = true;
      this.selectedUserIndex = 0;
    }
    if (this.mentioning && event.key === 'Enter') {
      event.stopPropagation();
      event.preventDefault();
      if (this.selectedUserIndex !== null) {
        const user = this.filteredUsers[this.selectedUserIndex];
        this.addMention(user);
      }
    }
  }
  
  public reset() {
    this.inputSignal.set('');
    this.editableInput.nativeElement.innerHTML = '';
    this.mentioning = false;
    this.selectedUserIndex = 0;
    this.cdr.markForCheck();
  }
}
